<script setup>
import { computed, ref, onMounted, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import {
  formatCapitalSocial,
  companyTipo,
} from 'dashboard/helper/companyHelper';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  selectedCompany: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToCompaniesList', 'updated', 'switchTab']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteDialogRef = ref(null);
const avatarFile = ref(null);
const avatarUrl = ref('');
const isUpdating = ref(false);
const isDeleting = ref(false);

const FORM_CONFIG = {
  NAME: { field: 'name', required: true },
  DOMAIN: { field: 'domain' },
  PHONE: { field: 'phone' },
  WHATSAPP_PHONE: { field: 'whatsappPhone' },
  WEBSITE: { field: 'website' },
  INDUSTRY: { field: 'industry' },
  EMPLOYEE_COUNT: { field: 'employeeCount', type: 'number' },
  ANNUAL_REVENUE: { field: 'annualRevenue', type: 'number' },
  ADDRESS: { field: 'addressLine1' },
  ADDRESS_LINE2: { field: 'addressLine2' },
  COMPLEMENT: { field: 'complement' },
  CITY: { field: 'city' },
  STATE: { field: 'state' },
  POSTAL_CODE: { field: 'postalCode' },
  DESCRIPTION: { field: 'description', type: 'textarea' },
};

const defaultState = {
  id: 0,
  name: '',
  domain: '',
  phone: '',
  whatsappPhone: '',
  website: '',
  industry: '',
  employeeCount: '',
  annualRevenue: '',
  addressLine1: '',
  addressLine2: '',
  complement: '',
  city: '',
  state: '',
  postalCode: '',
  description: '',
  country: 'BR',
  cnpj: '',
  taxId: '',
};

const state = reactive({ ...defaultState });

// País de cadastro da empresa + documento fiscal. No Brasil = CNPJ; fora, o
// documento do país (NIF, EIN, CIF...). Lista focada nos mercados que atendemos.
const COUNTRIES = [
  { code: 'BR', name: 'Brasil' },
  { code: 'PT', name: 'Portugal' },
  { code: 'US', name: 'Estados Unidos' },
  { code: 'ES', name: 'Espanha' },
  { code: 'AR', name: 'Argentina' },
  { code: 'MX', name: 'México' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colômbia' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'FR', name: 'França' },
  { code: 'DE', name: 'Alemanha' },
  { code: 'IT', name: 'Itália' },
  { code: 'AO', name: 'Angola' },
  { code: 'MZ', name: 'Moçambique' },
];
const DOC_LABELS = {
  BR: 'CNPJ',
  PT: 'NIF',
  US: 'EIN / Tax ID',
  ES: 'CIF / NIF',
  GB: 'Company Number',
  AR: 'CUIT',
  MX: 'RFC',
};

const isBrazil = computed(() => (state.country || 'BR') === 'BR');
const documentLabel = computed(
  () => DOC_LABELS[state.country] || t('COMPANY_DETAILS.FORM.TAX_ID.LABEL')
);
// O campo de documento escreve no CNPJ (Brasil) ou no tax_id (demais países).
const documentValue = computed({
  get: () => (isBrazil.value ? state.cnpj : state.taxId),
  set: value => {
    if (isBrazil.value) state.cnpj = value;
    else state.taxId = value;
  },
});

const validationRules = {
  name: { required },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const prepareStateBasedOnProps = () => {
  if (!props.selectedCompany) return;

  const {
    id,
    name = '',
    domain = '',
    phone = '',
    whatsappPhone = '',
    website = '',
    industry = '',
    employeeCount = '',
    annualRevenue = '',
    addressLine1 = '',
    addressLine2 = '',
    complement = '',
    city = '',
    state: companyState = '',
    postalCode = '',
    description = '',
    country = 'BR',
    cnpj = '',
    taxId = '',
  } = props.selectedCompany;

  Object.assign(state, {
    id,
    name,
    domain,
    phone,
    whatsappPhone,
    website,
    industry,
    employeeCount: employeeCount || '',
    annualRevenue: annualRevenue || '',
    addressLine1,
    addressLine2,
    complement,
    city,
    state: companyState,
    postalCode,
    description,
    country: country || 'BR',
    cnpj: cnpj || '',
    taxId: taxId || '',
  });
};

const createdAt = computed(() => {
  const timestamp = props.selectedCompany?.createdAt;
  if (!timestamp || timestamp === 0) return '';
  try {
    return dynamicTime(timestamp);
  } catch {
    return '';
  }
});

const companyOpeningDate = computed(() => {
  const date =
    props.selectedCompany?.additionalAttributes?.data_inicio_atividade;
  if (!date) return null;
  const parts = date.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return date;
});

const companyAge = computed(() => {
  const date =
    props.selectedCompany?.additionalAttributes?.data_inicio_atividade;
  if (!date) return null;
  const start = new Date(date);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years > 0)
    return `${years} ano${years > 1 ? 's' : ''}${months > 0 ? ` e ${months} ${months > 1 ? 'meses' : 'mês'}` : ''}`;
  return months > 0
    ? `${months} ${months > 1 ? 'meses' : 'mês'}`
    : 'Menos de 1 mês';
});

const companyCapitalSocial = computed(() =>
  formatCapitalSocial(
    props.selectedCompany?.additionalAttributes?.capital_social
  )
);

const companyNaturezaJuridica = computed(
  () => props.selectedCompany?.additionalAttributes?.natureza_juridica
);

const companyTipoLabel = computed(() =>
  companyTipo(props.selectedCompany?.additionalAttributes)
);

const companyAtividadePrincipal = computed(
  () => props.selectedCompany?.additionalAttributes?.cnae_fiscal_descricao
);

const companyMatrizFilial = computed(
  () => props.selectedCompany?.additionalAttributes?.matriz_filial
);

const companySimples = computed(() => {
  const val = props.selectedCompany?.additionalAttributes?.opcao_pelo_simples;
  if (val === true) return 'Sim';
  if (val === false) return 'Não';
  return null;
});

const companySocios = computed(() => {
  const qsa = props.selectedCompany?.additionalAttributes?.qsa;
  if (!qsa || !qsa.length) return [];
  return qsa;
});

// Decisor = sócio administrador (ou o 1º), espelhando a regra do backend
// (navi/contact_enrichment_service). Só aparece se a empresa foi enriquecida.
const companyDecisor = computed(() => {
  const qsa = companySocios.value;
  if (!qsa.length) return null;
  const admin = qsa.find(s => /administrador/i.test(s.qualificacao || ''));
  return admin || qsa[0];
});

// Data de entrada na sociedade pode vir como YYYY-MM-DD ou DD/MM/YYYY.
const formatSocioDate = value => {
  if (!value) return '';
  const parts = String(value).split(/[-/]/);
  if (parts.length !== 3) return value;
  return parts[0].length === 4 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
};

// Iniciais para o avatar do sócio (mesma ideia do Avatar por nome).
const socioInitials = name =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const cnpjCnaesSecundarios = computed(() => {
  const cnaes = props.selectedCompany?.additionalAttributes?.cnaes_secundarios;
  if (!cnaes || !cnaes.length) return [];
  return cnaes;
});

const companyCnpj = computed(() => props.selectedCompany?.cnpj);

const hasCnpjData = computed(
  () =>
    companyCnpj.value ||
    companyOpeningDate.value ||
    companyCapitalSocial.value ||
    companyNaturezaJuridica.value ||
    companyTipoLabel.value ||
    companyAtividadePrincipal.value ||
    companyMatrizFilial.value ||
    companySimples.value ||
    companySocios.value.length > 0
);

const contactsCount = computed(() => props.selectedCompany?.contactsCount || 0);

const dealsCount = computed(() => props.selectedCompany?.dealsCount || 0);

const avatarSrc = computed(() => {
  return avatarUrl.value || props.selectedCompany?.avatarUrl || '';
});

const editDetailsForm = computed(() =>
  Object.entries(FORM_CONFIG).map(([key, config]) => ({
    key,
    field: config.field,
    type: config.type || 'text',
    required: config.required || false,
    placeholder: t(`COMPANY_DETAILS.FORM.${key}.PLACEHOLDER`),
    label: t(`COMPANY_DETAILS.FORM.${key}.LABEL`),
  }))
);

const getFormBinding = key => {
  const config = FORM_CONFIG[key];
  if (!config) return null;

  return computed({
    get: () => state[config.field]?.toString() || '',
    set: value => {
      state[config.field] = value;
    },
  });
};

const getMessageType = key => {
  const config = FORM_CONFIG[key];
  if (!config?.required) return 'info';
  return v$.value[config.field]?.$error ? 'error' : 'info';
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('companies/update', {
      id: state.id,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('COMPANY_DETAILS.AVATAR.UPLOAD.SUCCESS'));
    emit('updated');
  } catch {
    useAlert(t('COMPANY_DETAILS.AVATAR.UPLOAD.ERROR'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedCompany?.id) {
      await store.dispatch('companies/deleteAvatar', props.selectedCompany.id);
      useAlert(t('COMPANY_DETAILS.AVATAR.DELETE.SUCCESS'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    emit('updated');
  } catch (error) {
    useAlert(error.message || t('COMPANY_DETAILS.AVATAR.DELETE.ERROR'));
  }
};

const updateCompany = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isUpdating.value = true;
  try {
    await store.dispatch('companies/update', {
      id: state.id,
      name: state.name,
      domain: state.domain,
      phone: state.phone,
      whatsapp_phone: state.whatsappPhone,
      website: state.website,
      industry: state.industry,
      employee_count: state.employeeCount,
      annual_revenue: state.annualRevenue,
      address_line1: state.addressLine1,
      address_line2: state.addressLine2,
      complement: state.complement,
      city: state.city,
      state: state.state,
      postal_code: state.postalCode,
      description: state.description,
      country: state.country,
      cnpj: isBrazil.value ? state.cnpj : '',
      tax_id: isBrazil.value ? '' : state.taxId,
    });
    useAlert(t('COMPANY_DETAILS.UPDATE_SUCCESS'));
    emit('updated');
  } catch (error) {
    useAlert(t('COMPANY_DETAILS.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const openConfirmDeleteDialog = () => {
  confirmDeleteDialogRef.value?.open();
};

const handleDelete = async () => {
  isDeleting.value = true;
  try {
    await store.dispatch('companies/delete', props.selectedCompany.id);
    useAlert(t('COMPANY_DETAILS.DELETE_SUCCESS'));
    confirmDeleteDialogRef.value?.close();
    emit('goToCompaniesList');
  } catch (error) {
    useAlert(t('COMPANY_DETAILS.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

watch(
  () => props.selectedCompany?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

onMounted(() => {
  prepareStateBasedOnProps();
});
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <!-- Header: Avatar and basic info -->
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc"
        :name="selectedCompany?.name || ''"
        :size="72"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-2">
          <h3
            class="text-lg font-semibold tracking-tight text-n-slate-12 leading-tight"
          >
            {{ selectedCompany?.name }}
          </h3>
          <span
            v-if="companyTipoLabel || selectedCompany?.companyType"
            class="inline-flex items-center px-2 py-0.5 text-xs font-medium capitalize rounded-full bg-n-blue-3 text-n-blue-11"
          >
            {{ companyTipoLabel || selectedCompany?.companyType }}
          </span>
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedCompany?.domain"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-lucide-globe text-n-slate-10 size-4" />
            <a
              :href="`https://${selectedCompany.domain}`"
              target="_blank"
              rel="noopener noreferrer"
              class="hover:text-n-brand hover:underline"
            >
              {{ selectedCompany.domain }}
            </a>
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span class="i-lucide-calendar text-n-slate-10 size-4" />
            {{ $t('COMPANY_DETAILS.CREATED_AT', { date: createdAt }) }}
          </span>
          <span
            v-if="companyOpeningDate"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-lucide-building text-n-slate-10 size-4" />
            {{ $t('COMPANY_DETAILS.FOUNDED_AT') }}: {{ companyOpeningDate }}
            <template v-if="companyAge"> ({{ companyAge }}) </template>
          </span>
          <span
            v-if="companyCapitalSocial"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-lucide-banknote text-n-slate-10 size-4" />
            {{ $t('COMPANY_DETAILS.CAPITAL_SOCIAL') }}:
            {{ companyCapitalSocial }}
          </span>
          <span
            v-if="companyNaturezaJuridica"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-lucide-scale text-n-slate-10 size-4" />
            {{ $t('COMPANY_DETAILS.NATUREZA_JURIDICA') }}:
            {{ companyNaturezaJuridica }}
          </span>
          <div class="flex items-center gap-3 mt-1">
            <button
              class="inline-flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-brand cursor-pointer"
              @click="emit('switchTab', 'contacts')"
            >
              <span class="i-lucide-contact text-n-slate-10 size-4" />
              {{
                $t('COMPANY_DETAILS.CONTACTS_COUNT', { count: contactsCount })
              }}
            </button>
            <button
              class="inline-flex items-center gap-1 text-sm text-n-slate-11 hover:text-n-brand cursor-pointer"
              @click="emit('switchTab', 'deals')"
            >
              <span class="i-lucide-briefcase text-n-slate-10 size-4" />
              {{ $t('COMPANY_DETAILS.DEALS_COUNT', { count: dealsCount }) }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Form -->
    <div class="flex flex-col items-start gap-6 w-full">
      <div class="flex flex-col items-start gap-2 w-full">
        <span
          class="py-1 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ t('COMPANY_DETAILS.FORM.TITLE') }}
        </span>
        <div class="grid w-full grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <template v-for="item in editDetailsForm" :key="item.key">
            <label
              v-if="item.type !== 'textarea'"
              class="flex flex-col w-full gap-1"
            >
              <span class="text-xs font-medium text-n-slate-11">
                {{ item.label }}
              </span>
              <Input
                v-model="getFormBinding(item.key).value"
                :placeholder="item.placeholder"
                :type="item.type"
                :message-type="getMessageType(item.key)"
                custom-input-class="h-8 !pt-1 !pb-1"
                class="w-full"
                @blur="item.required && v$[item.field]?.$touch()"
              />
            </label>
          </template>

          <!-- País de cadastro da empresa -->
          <label class="flex flex-col w-full gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('COMPANY_DETAILS.FORM.COUNTRY.LABEL') }}
            </span>
            <select
              v-model="state.country"
              class="w-full h-8 px-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            >
              <option v-for="c in COUNTRIES" :key="c.code" :value="c.code">
                {{ c.name }}
              </option>
            </select>
          </label>

          <!-- Documento fiscal: CNPJ (Brasil) ou o documento do país -->
          <label class="flex flex-col w-full gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ documentLabel }}
            </span>
            <Input
              v-model="documentValue"
              :placeholder="documentLabel"
              custom-input-class="h-8 !pt-1 !pb-1"
              class="w-full"
            />
          </label>
        </div>
        <!-- Description textarea full width -->
        <label class="flex flex-col w-full gap-1 mt-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('COMPANY_DETAILS.FORM.DESCRIPTION.LABEL') }}
          </span>
          <Input
            v-model="state.description"
            :placeholder="t('COMPANY_DETAILS.FORM.DESCRIPTION.PLACEHOLDER')"
            type="textarea"
            custom-input-class="min-h-[80px]"
            class="w-full"
          />
        </label>
      </div>
      <Button
        :label="t('COMPANY_DETAILS.ACTIONS.UPDATE')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateCompany"
      />
    </div>

    <!-- CNPJ Data Section -->
    <div v-if="hasCnpjData" class="flex flex-col items-start gap-2 w-full">
      <span
        class="py-1 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
      >
        {{ t('COMPANY_DETAILS.CNPJ_DATA.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
        <div v-if="companyCnpj" class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-10">{{
            t('COMPANIES.FORM.CNPJ.LABEL')
          }}</span>
          <span class="text-sm text-n-slate-12">{{ companyCnpj }}</span>
        </div>
        <div v-if="companyOpeningDate" class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-10">
            {{ t('COMPANY_DETAILS.CNPJ_DATA.OPENING_DATE') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ companyOpeningDate }}
            <template v-if="companyAge"> ({{ companyAge }}) </template>
          </span>
        </div>
        <div v-if="companyCapitalSocial" class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-10">
            {{ t('COMPANY_DETAILS.CNPJ_DATA.CAPITAL_SOCIAL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ companyCapitalSocial }}
          </span>
        </div>
        <div
          v-if="companyTipoLabel || companyNaturezaJuridica"
          class="flex flex-col gap-0.5"
        >
          <span class="text-xs text-n-slate-10">
            {{ t('COMPANY_DETAILS.CNPJ_DATA.TIPO_EMPRESA') }}
          </span>
          <span
            v-if="companyTipoLabel"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ companyTipoLabel }}
          </span>
          <span
            v-if="companyNaturezaJuridica"
            :class="
              companyTipoLabel
                ? 'text-xs text-n-slate-10'
                : 'text-sm text-n-slate-12'
            "
          >
            {{ companyNaturezaJuridica }}
          </span>
        </div>
        <div
          v-if="companyAtividadePrincipal"
          class="flex flex-col gap-0.5 sm:col-span-2"
        >
          <span class="text-xs text-n-slate-10">
            {{ t('COMPANY_DETAILS.CNPJ_DATA.ATIVIDADE_PRINCIPAL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ companyAtividadePrincipal }}
          </span>
        </div>
        <div v-if="companyMatrizFilial" class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-10">
            {{ t('COMPANY_DETAILS.CNPJ_DATA.MATRIZ_FILIAL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ companyMatrizFilial }}
          </span>
        </div>
        <div v-if="companySimples" class="flex flex-col gap-0.5">
          <span class="text-xs text-n-slate-10">
            {{ t('COMPANY_DETAILS.CNPJ_DATA.SIMPLES_NACIONAL') }}
          </span>
          <span class="text-sm text-n-slate-12">{{ companySimples }}</span>
        </div>
        <div
          v-if="cnpjCnaesSecundarios.length"
          class="flex flex-col gap-0.5 sm:col-span-2"
        >
          <span class="text-xs text-n-slate-10">
            {{ t('COMPANY_DETAILS.CNPJ_DATA.ATIVIDADES_SECUNDARIAS') }}
          </span>
          <div class="flex flex-wrap gap-1">
            <span
              v-for="(cnae, idx) in cnpjCnaesSecundarios"
              :key="idx"
              class="text-xs px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ cnae.descricao }}
            </span>
          </div>
        </div>
        <!-- Quadro societário: um card por sócio; o decisor (sócio
             administrador) recebe destaque em cor + coroa, sem duplicar. -->
        <div
          v-if="companySocios.length"
          class="flex flex-col gap-2 sm:col-span-2"
        >
          <span
            class="text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            {{ t('COMPANY_DETAILS.CNPJ_DATA.SOCIOS') }}
          </span>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div
              v-for="(socio, idx) in companySocios"
              :key="idx"
              class="flex flex-col gap-2 p-3 border rounded-lg"
              :class="
                socio === companyDecisor
                  ? 'border-n-iris-5 bg-n-iris-2'
                  : 'border-n-weak bg-n-solid-1'
              "
            >
              <div class="flex items-center gap-2.5">
                <span
                  class="flex items-center justify-center text-xs font-semibold rounded-full shrink-0 size-8"
                  :class="
                    socio === companyDecisor
                      ? 'bg-n-iris-4 text-n-iris-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{ socioInitials(socio.nome) }}
                </span>
                <div class="flex flex-col min-w-0">
                  <span
                    class="inline-flex items-center gap-1 text-sm font-medium truncate text-n-slate-12"
                  >
                    {{ socio.nome }}
                    <span
                      v-if="socio === companyDecisor"
                      class="i-lucide-crown size-3.5 text-n-iris-11 shrink-0"
                      :title="t('COMPANY_DETAILS.CNPJ_DATA.DECISOR')"
                    />
                  </span>
                  <span
                    v-if="socio.qualificacao"
                    class="text-xs truncate"
                    :class="
                      socio === companyDecisor
                        ? 'text-n-iris-11'
                        : 'text-n-slate-11'
                    "
                  >
                    {{ socio.qualificacao }}
                  </span>
                </div>
              </div>
              <div
                v-if="socio.faixa_etaria || socio.data_entrada"
                class="flex flex-wrap gap-x-4 gap-y-1 pl-[42px]"
              >
                <span
                  v-if="socio.faixa_etaria"
                  class="inline-flex items-center gap-1 text-xs text-n-slate-10"
                >
                  <span class="i-lucide-user-round size-3" />
                  {{ socio.faixa_etaria }}
                </span>
                <span
                  v-if="socio.data_entrada"
                  class="inline-flex items-center gap-1 text-xs text-n-slate-10"
                >
                  <span class="i-lucide-calendar-days size-3" />
                  {{ t('COMPANY_DETAILS.CNPJ_DATA.MEMBER_SINCE') }}
                  {{ formatSocioDate(socio.data_entrada) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Section -->
    <div
      class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
    >
      <div class="flex flex-col gap-2">
        <h6 class="text-base font-medium text-n-slate-12">
          {{ t('COMPANY_DETAILS.DELETE.TITLE') }}
        </h6>
        <span class="text-sm text-n-slate-11">
          {{ t('COMPANY_DETAILS.DELETE.DESCRIPTION') }}
        </span>
      </div>
      <Button
        :label="t('COMPANY_DETAILS.DELETE.BUTTON')"
        color="ruby"
        @click="openConfirmDeleteDialog"
      />
    </div>

    <!-- Delete Confirmation Dialog -->
    <Dialog
      ref="confirmDeleteDialogRef"
      type="alert"
      :title="t('COMPANY_DETAILS.DELETE.CONFIRM_TITLE')"
      :description="
        t('COMPANY_DETAILS.DELETE.CONFIRM_MESSAGE', {
          name: selectedCompany?.name,
        })
      "
      :confirm-button-label="t('COMPANY_DETAILS.DELETE.CONFIRM_BUTTON')"
      :cancel-button-label="t('COMPANY_DETAILS.DELETE.CANCEL_BUTTON')"
      :is-loading="isDeleting"
      @confirm="handleDelete"
    />
  </div>
</template>
